import { useState, useEffect } from 'react';
import { X, Copy, Check, UserPlus, Link2, Trash2, Mail, Eye, Edit3, Users, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

export const ShareModal = ({ isOpen, onClose, whiteboardId, whiteboardName, token }) => {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState('edit');
  const [isLoading, setIsLoading] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [owner, setOwner] = useState(null);
  const [shareToken, setShareToken] = useState(null);
  const [sharePermission, setSharePermission] = useState('view');
  const [copied, setCopied] = useState(false);
  const [linkEnabled, setLinkEnabled] = useState(false);

  // Fetch collaborators when modal opens
  useEffect(() => {
    if (isOpen && whiteboardId) {
      fetchCollaborators();
    }
  }, [isOpen, whiteboardId]);

  const fetchCollaborators = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/collaborators`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setOwner(data.owner);
        setCollaborators(data.collaborators || []);
        setShareToken(data.share_token);
        setSharePermission(data.share_permission || 'view');
        setLinkEnabled(!!data.share_token);
      }
    } catch (error) {
      console.error('Error fetching collaborators:', error);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim(), permission }),
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        setEmail('');
        fetchCollaborators();
      } else {
        toast.error(data.detail || 'Failed to invite user');
      }
    } catch (error) {
      toast.error('Failed to invite user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/share/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Collaborator removed');
        fetchCollaborators();
      } else {
        toast.error('Failed to remove collaborator');
      }
    } catch (error) {
      toast.error('Failed to remove collaborator');
    }
  };

  const handleToggleShareLink = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/share-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          enabled: !linkEnabled,
          permission: sharePermission 
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.share_token) {
          setShareToken(data.share_token);
          setLinkEnabled(true);
          toast.success('Share link enabled');
        } else {
          setShareToken(null);
          setLinkEnabled(false);
          toast.success('Share link disabled');
        }
      }
    } catch (error) {
      toast.error('Failed to update share link');
    }
  };

  const handleUpdateLinkPermission = async (newPermission) => {
    setSharePermission(newPermission);
    if (linkEnabled) {
      try {
        await fetch(`${BACKEND_URL}/api/whiteboards/${whiteboardId}/share-link`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ enabled: true, permission: newPermission }),
        });
      } catch (error) {
        console.error('Error updating link permission:', error);
      }
    }
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/whiteboard/shared/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} />
            Share &ldquo;{whiteboardName}&rdquo;
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Invite by email */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Invite people</label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                  data-testid="share-email-input"
                />
              </div>
              <Select value={permission} onValueChange={setPermission}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="view">
                    <span className="flex items-center gap-1">
                      <Eye size={14} /> View
                    </span>
                  </SelectItem>
                  <SelectItem value="edit">
                    <span className="flex items-center gap-1">
                      <Edit3 size={14} /> Edit
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleInvite} 
                disabled={isLoading}
                data-testid="share-invite-btn"
              >
                <UserPlus size={16} className="mr-1" />
                Invite
              </Button>
            </div>
          </div>

          {/* Share link section */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Globe size={16} />
                Public share link
              </label>
              <Button
                variant={linkEnabled ? "default" : "outline"}
                size="sm"
                onClick={handleToggleShareLink}
                data-testid="share-link-toggle"
              >
                {linkEnabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
            
            {linkEnabled && shareToken && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={`${window.location.origin}/whiteboard/shared/${shareToken}`}
                    readOnly
                    className="text-sm bg-gray-50"
                    data-testid="share-link-input"
                  />
                  <Button variant="outline" size="icon" onClick={copyShareLink} data-testid="copy-link-btn">
                    {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Anyone with the link can</span>
                  <Select value={sharePermission} onValueChange={handleUpdateLinkPermission}>
                    <SelectTrigger className="w-[90px] h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">view</SelectItem>
                      <SelectItem value="edit">edit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Collaborators list */}
          <div className="space-y-3 pt-4 border-t">
            <label className="text-sm font-medium text-gray-700">People with access</label>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {/* Owner */}
              {owner && (
                <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: owner.avatar_color }}
                    >
                      {getInitials(owner.name)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{owner.name}</p>
                      <p className="text-xs text-gray-500">{owner.email}</p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-1 rounded">
                    Owner
                  </span>
                </div>
              )}

              {/* Collaborators */}
              {collaborators.map((collab) => (
                <div 
                  key={collab.user_id} 
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                      style={{ backgroundColor: collab.avatar_color || '#6366F1' }}
                    >
                      {getInitials(collab.name)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{collab.name}</p>
                      <p className="text-xs text-gray-500">{collab.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 capitalize flex items-center gap-1">
                      {collab.permission === 'edit' ? <Edit3 size={12} /> : <Eye size={12} />}
                      {collab.permission}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleRemoveCollaborator(collab.user_id)}
                      data-testid={`remove-collab-${collab.user_id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}

              {collaborators.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No collaborators yet. Invite someone to start collaborating!
                </p>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
